/**
 * Innovation Pathways — additive section for the BSGOTECH homepage.
 *
 * Two responsive cards (side-by-side on desktop, stacked on mobile) that
 * illustrate proposed exploration tracks in Health and Industry. Every
 * image opens a full-screen lightbox on click. Fully DE / EN / PT.
 *
 * ADDITIVE only: does NOT remove or modify any existing section.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, HeartPulse, Cpu, ArrowUpRight, FileText } from "lucide-react";
import { LangCtx } from "@/i18n/neunexus";

const P = {
  bg:      "#080B12",
  bg2:     "#0A0F1A",
  border:  "rgba(255,255,255,0.08)",
  surface: "rgba(255,255,255,0.03)",
  ink:     "#E8ECF0",
  dim:     "rgba(232,236,240,0.68)",
  silver:  "#C0C7CE",
  green:   "#22C55E",
  blue:    "#3B82F6",
  rose:    "#F43F5E",
  amber:   "#F59E0B",
};

const DATA = {
  en: {
    eyebrow: "Innovation Pathways",
    title: "Two proposed exploration tracks.",
    subtitle: "Concept and pre-validation stage. Communicated with transparency about scope, responsibilities and status.",
    cards: [
      {
        id: "health",
        icon: HeartPulse,
        accent: P.rose,
        badge: "NeuNexus Health Intelligence · RENALIX",
        title: "NeuNexus Health Intelligence",
        subtitle: "RENALIX — Responsible Clinical Intelligence",
        desc: "RENALIX is a proposed clinical intelligence platform designed to connect renal laboratory trends, nursing observations and professional workflows through secure integration and human-controlled clinical review.",
        status: "Concept & Pre-Validation Stage",
        disclaimer: "RENALIX is not currently certified or approved for routine clinical use. Healthcare professionals retain responsibility for all clinical decisions.",
        cta: "View RENALIX Executive Concept",
        pdf: "/downloads/NeuNexus_Health_Intelligence_Executive_Concept.pdf",
        image: "/innovation/renalix-health-intelligence.webp",
        alt: "RENALIX platform architecture — Responsible AI, Human Control, Better Clinical Insight. Diagram showing Discovery, MVP, Usability, Retrospective Validation, Prospective Pilot and Regulatory Decision steps connecting RENALIX Pro (physicians) and RENALIX Care (nursing) through GDPR-by-design integration.",
      },
      {
        id: "industrial",
        icon: Cpu,
        accent: P.amber,
        badge: "NeuNexus Industrial Ecosystem",
        title: "NeuNexus Industrial Ecosystem",
        subtitle: "Physical AI for Modular Energy Systems · NEURA Robotics focus",
        desc: "A proposed robotic pilot leveraging NEURA Robotics cognitive robots for assisted handling, visual inspection and traceability of modular clean-energy components, beginning with one safe and measurable industrial task.",
        status: "Proposed for Initial Technical Evaluation",
        disclaimer: "NEURA Robotics and other technology providers, responsibilities and partnerships remain subject to technical assessment and written agreement.",
        cta: "View Industrial Pilot Executive Concept",
        pdf: "/downloads/NeuNexus_Industrial_Ecosystem_Executive_Concept.pdf",
        image: "/innovation/industrial-ecosystem.webp",
        alt: "Proposed NEURA Gym Process for NeuNexus — seven-step robotic pilot from Use Case Definition to Pilot Deployment, showing a collaborative robotic arm handling modular NeuGreen battery cabinets with edge dashboards for cell status, traceability log and visual inspection.",
      },
    ],
    zoom: "Click to expand",
    close: "Close",
  },
  de: {
    eyebrow: "Innovationspfade",
    title: "Zwei vorgeschlagene Erkundungspfade.",
    subtitle: "Konzept- und Vorvalidierungsphase. Transparent kommuniziert hinsichtlich Umfang, Verantwortlichkeiten und Status.",
    cards: [
      {
        id: "health",
        icon: HeartPulse,
        accent: P.rose,
        badge: "NeuNexus Health Intelligence · RENALIX",
        title: "NeuNexus Health Intelligence",
        subtitle: "RENALIX — Verantwortungsvolle klinische Intelligenz",
        desc: "RENALIX ist eine vorgeschlagene klinische Intelligenzplattform, die renale Labortrends, pflegerische Beobachtungen und professionelle Workflows über sichere Integration und menschengesteuerte klinische Überprüfung verbindet.",
        status: "Konzept- und Vorvalidierungsphase",
        disclaimer: "RENALIX ist derzeit nicht zertifiziert und nicht für den klinischen Routineeinsatz zugelassen. Die Verantwortung für alle klinischen Entscheidungen liegt bei den medizinischen Fachkräften.",
        cta: "RENALIX Executive Concept ansehen",
        pdf: "/downloads/NeuNexus_Health_Intelligence_Executive_Concept.pdf",
        image: "/innovation/renalix-health-intelligence.webp",
        alt: "RENALIX Plattform-Architektur — Responsible AI, Human Control, Better Clinical Insight. Diagramm mit Discovery, MVP, Usability, retrospektiver Validierung, prospektivem Piloten und Regulatory Decision, das RENALIX Pro (Ärzte) und RENALIX Care (Pflege) über GDPR-by-design-Integration verbindet.",
      },
      {
        id: "industrial",
        icon: Cpu,
        accent: P.amber,
        badge: "NeuNexus Industrial Ecosystem",
        title: "NeuNexus Industrial Ecosystem",
        subtitle: "Physical AI für modulare Energiesysteme · Fokus NEURA Robotics",
        desc: "Ein vorgeschlagener Robotik-Pilot, der auf kognitive Roboter von NEURA Robotics setzt, für assistierte Handhabung, visuelle Inspektion und Rückverfolgbarkeit modularer Clean-Energy-Komponenten, beginnend mit einer sicheren und messbaren industriellen Aufgabe.",
        status: "Für erste technische Bewertung vorgeschlagen",
        disclaimer: "NEURA Robotics und weitere Technologieanbieter, Verantwortlichkeiten und Partnerschaften unterliegen der technischen Bewertung und schriftlichen Vereinbarung.",
        cta: "Industrial Pilot Executive Concept ansehen",
        pdf: "/downloads/NeuNexus_Industrial_Ecosystem_Executive_Concept.pdf",
        image: "/innovation/industrial-ecosystem.webp",
        alt: "Vorgeschlagener NEURA Gym Process für NeuNexus — siebenstufiger Robotik-Pilot von der Use-Case-Definition bis zum Pilot Deployment mit einem kollaborativen Roboterarm, der modulare NeuGreen-Batterieschränke handhabt, und Edge-Dashboards für Zellstatus, Traceability-Log und visuelle Inspektion.",
      },
    ],
    zoom: "Zum Vergrößern klicken",
    close: "Schließen",
  },
  pt: {
    eyebrow: "Caminhos de Inovação",
    title: "Dois percursos de exploração propostos.",
    subtitle: "Fase de conceito e pré-validação. Comunicados com transparência sobre âmbito, responsabilidades e estado.",
    cards: [
      {
        id: "health",
        icon: HeartPulse,
        accent: P.rose,
        badge: "NeuNexus Health Intelligence · RENALIX",
        title: "NeuNexus Health Intelligence",
        subtitle: "RENALIX — Inteligência Clínica Responsável",
        desc: "A RENALIX é uma plataforma de inteligência clínica proposta, desenhada para ligar tendências laboratoriais renais, observações de enfermagem e fluxos de trabalho profissionais através de integração segura e revisão clínica controlada por humanos.",
        status: "Fase de Conceito e Pré-Validação",
        disclaimer: "A RENALIX não está actualmente certificada nem aprovada para uso clínico de rotina. Os profissionais de saúde mantêm a responsabilidade por todas as decisões clínicas.",
        cta: "Ver Executive Concept RENALIX",
        pdf: "/downloads/NeuNexus_Health_Intelligence_Executive_Concept.pdf",
        image: "/innovation/renalix-health-intelligence.webp",
        alt: "Arquitectura da plataforma RENALIX — Responsible AI, Human Control, Better Clinical Insight. Diagrama com Discovery, MVP, Usability, validação retrospectiva, piloto prospectivo e Regulatory Decision, ligando RENALIX Pro (médicos) e RENALIX Care (enfermagem) através de integração GDPR-by-design.",
      },
      {
        id: "industrial",
        icon: Cpu,
        accent: P.amber,
        badge: "NeuNexus Industrial Ecosystem",
        title: "NeuNexus Industrial Ecosystem",
        subtitle: "Physical AI para Sistemas Modulares de Energia · foco NEURA Robotics",
        desc: "Um piloto robótico proposto que utiliza robots cognitivos da NEURA Robotics para manipulação assistida, inspecção visual e rastreabilidade de componentes modulares de energia limpa, começando por uma tarefa industrial segura e mensurável.",
        status: "Proposta para Avaliação Técnica Inicial",
        disclaimer: "A NEURA Robotics e demais fornecedores de tecnologia, responsabilidades e parcerias permanecem sujeitos a avaliação técnica e acordo escrito.",
        cta: "Ver Industrial Pilot Executive Concept",
        pdf: "/downloads/NeuNexus_Industrial_Ecosystem_Executive_Concept.pdf",
        image: "/innovation/industrial-ecosystem.webp",
        alt: "Proposta NEURA Gym Process para NeuNexus — piloto robótico de sete passos, da definição de use case ao Pilot Deployment, com braço robótico colaborativo a manipular armários modulares de bateria NeuGreen e edge dashboards para estado das células, log de rastreabilidade e inspecção visual.",
      },
    ],
    zoom: "Clicar para expandir",
    close: "Fechar",
  },
};

const useI = () => {
  const { lang } = React.useContext(LangCtx);
  return DATA[lang] || DATA.en;
};

export default function InnovationPathways() {
  const t = useI();
  const [expanded, setExpanded] = React.useState(null);

  // Prevent body scroll while modal is open
  React.useEffect(() => {
    if (expanded) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [expanded]);

  // Close on ESC
  React.useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setExpanded(null); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <section id="innovation-pathways" data-testid="innovation-pathways"
      className="relative border-t" style={{ background: P.bg, borderColor: P.border }}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 30% 20%, ${P.rose}0f 0%, transparent 55%), radial-gradient(circle at 70% 80%, ${P.amber}0f 0%, transparent 55%)`,
      }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <div className="text-[10px] uppercase tracking-[0.42em] font-semibold" style={{ color: P.green }}>
            {t.eyebrow}
          </div>
          <h2 className="mt-4 font-semibold tracking-tight text-[26px] sm:text-[34px] lg:text-[42px] leading-[1.05]"
            style={{ color: "#fff", letterSpacing: "-0.02em" }}>
            {t.title}
          </h2>
          <p className="mt-4 max-w-2xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
            {t.subtitle}
          </p>
        </motion.div>

        {/* Responsive grid: 1 col mobile, 2 cols desktop */}
        <div className="mt-12 grid lg:grid-cols-2 gap-5 lg:gap-6">
          {t.cards.map((c, i) => {
            const Ic = c.icon;
            return (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1, duration: 0.55 }}
                data-testid={`innovation-card-${c.id}`}
                className="rounded-xl overflow-hidden flex flex-col"
                style={{
                  background: `linear-gradient(180deg, ${P.bg2} 0%, ${P.bg} 100%)`,
                  border: `1px solid ${P.border}`,
                }}
              >
                <div style={{ height: 2, background: `linear-gradient(90deg, ${c.accent}66 0%, ${c.accent}00 100%)` }} aria-hidden="true" />

                {/* Image — click to expand */}
                <button
                  type="button"
                  onClick={() => setExpanded(c)}
                  data-testid={`innovation-image-btn-${c.id}`}
                  className="relative group block w-full text-left"
                  aria-label={t.zoom}
                >
                  <div className="relative overflow-hidden" style={{ aspectRatio: "16 / 10", background: P.bg2 }}>
                    <img
                      src={c.image}
                      alt={c.alt}
                      loading="lazy"
                      className="w-full h-full object-contain transition duration-500 group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition"
                      style={{ background: "linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.55) 100%)" }} aria-hidden="true" />
                    <div className="absolute bottom-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition text-[11px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-md"
                      style={{ background: "rgba(0,0,0,0.6)", border: `1px solid ${P.border}`, color: "#fff" }}>
                      <ZoomIn size={11} /> {t.zoom}
                    </div>
                  </div>
                </button>

                {/* Text */}
                <div className="p-6 lg:p-7 flex-1 flex flex-col">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: c.accent }}>
                    <Ic size={12} /> {c.badge}
                  </div>
                  <h3 className="mt-3 font-semibold tracking-tight text-[18px] lg:text-[20px]" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
                    {c.title}
                  </h3>
                  {c.subtitle && (
                    <div className="mt-1 text-[13px]" style={{ color: c.accent, opacity: 0.9 }}>
                      {c.subtitle}
                    </div>
                  )}
                  <p className="mt-4 text-[13px] leading-relaxed" style={{ color: P.ink, opacity: 0.9 }}>
                    {c.desc}
                  </p>

                  {c.status && (
                    <div className="mt-4 inline-flex items-center gap-1.5 self-start text-[10.5px] uppercase tracking-[0.2em] font-semibold px-2.5 py-1 rounded-md"
                      style={{ background: `${c.accent}18`, border: `1px solid ${c.accent}55`, color: c.accent }}
                      data-testid={`innovation-status-${c.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.accent }} />
                      {c.status}
                    </div>
                  )}

                  <div
                    data-testid={`innovation-disclaimer-${c.id}`}
                    className="mt-4 text-[11.5px] italic leading-relaxed p-3 rounded-md"
                    style={{ background: `${c.accent}0a`, border: `1px solid ${c.accent}33`, color: P.dim }}
                  >
                    {c.disclaimer}
                  </div>

                  {c.pdf && c.cta && (
                    <a
                      href={c.pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      data-testid={`innovation-cta-${c.id}`}
                      className="mt-6 inline-flex items-center gap-2 self-start px-4 py-2.5 text-[12px] font-semibold rounded-md transition hover:brightness-110"
                      style={{ background: `${c.accent}18`, border: `1px solid ${c.accent}66`, color: c.accent }}
                    >
                      <FileText size={13} />
                      {c.cta}
                      <ArrowUpRight size={12} />
                    </a>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
            style={{ background: "rgba(4,6,10,0.92)", backdropFilter: "blur(6px)" }}
            onClick={() => setExpanded(null)}
            data-testid="innovation-lightbox"
          >
            <motion.div
              initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-w-6xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setExpanded(null)}
                data-testid="innovation-lightbox-close"
                aria-label={t.close}
                className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 w-9 h-9 rounded-full flex items-center justify-center transition hover:bg-white/10 z-10"
                style={{ background: "rgba(8,11,18,0.95)", border: `1px solid ${P.border}`, color: "#fff" }}
              >
                <X size={16} />
              </button>
              <img
                src={expanded.image}
                alt={expanded.alt}
                className="w-full h-auto rounded-lg"
                style={{ maxHeight: "88vh", objectFit: "contain", boxShadow: "0 40px 100px -20px rgba(0,0,0,0.8)" }}
              />
              <div className="mt-3 text-[12px] uppercase tracking-[0.28em] text-center" style={{ color: P.silver }}>
                {expanded.badge}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
